'use client';

import { FileText } from 'lucide-react';
import { type ReactNode, useLayoutEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

type DocumentNavButtonProps = {
  isOpen: boolean;
  onClick: () => void;
  title: string;
};

/** One document entry in a reader's index. `aria-current` is what locates the open entry for scrolling. */
export function DocumentNavButton({ isOpen, onClick, title }: DocumentNavButtonProps) {
  return (
    <button
      aria-current={isOpen ? 'true' : undefined}
      className={cn(
        'hover:bg-accent focus-visible:ring-ring flex w-full items-start gap-2 rounded-md px-2 py-1 text-left text-xs transition-colors focus-visible:ring-2',
        isOpen && 'bg-primary/10 text-primary hover:bg-primary/10'
      )}
      onClick={onClick}
      type="button"
    >
      <FileText className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span className="min-w-0 flex-1 wrap-break-word">{title}</span>
    </button>
  );
}

type DocumentReaderNavProps = {
  activeKey: number | string;
  ariaLabel: string;
  children: ReactNode;
  heading: string;
};

/**
 * The chrome shared by every reader's document index: a labelled heading and a scrollport that pins
 * below the meeting tab strip. The document beside it is taller than the viewport, so the index has
 * to scroll within a viewport-high cap instead of scrolling away with the page.
 */
export function DocumentReaderNav({
  activeKey,
  ariaLabel,
  children,
  heading,
}: DocumentReaderNavProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  /**
   * Scrolls the index to the open entry inside its own scrollport. The index renders a whole agenda,
   * so the open document is usually well below the top it starts at.
   *
   * The open entry is located by its `aria-current` marker rather than by a ref, because the agenda
   * index is a recursive tree and a ref would have to be threaded through every level of it.
   *
   * Deliberately not `scrollIntoView`: that walks every ancestor scrollport, and would fight the
   * page-level scroll the reader performs in the same commit.
   */
  useLayoutEffect(() => {
    const container = scrollRef.current;
    const active = container?.querySelector('[aria-current="true"]');
    if (!container || !active) {
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const activeRect = active.getBoundingClientRect();
    const isFullyVisible =
      activeRect.top >= containerRect.top && activeRect.bottom <= containerRect.bottom;

    // Clicking a visible entry should leave the index where it is; only a jump from elsewhere —
    // the agenda body, a deep link — needs the entry brought into view, centred so the sections
    // around it stay readable as context.
    if (isFullyVisible) {
      return;
    }

    container.scrollTop +=
      activeRect.top - containerRect.top - (containerRect.height - activeRect.height) / 2;
  }, [activeKey]);

  return (
    <nav
      aria-label={ariaLabel}
      className="bg-background flex h-[calc(100vh-16px)] flex-col overflow-hidden"
    >
      <div className="border-b px-3 py-2">
        <h2 className="text-sm font-semibold">{heading}</h2>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-2" ref={scrollRef}>
        {children}
      </div>
    </nav>
  );
}
